import styled from "styled-components";
import {
  Modal,
  ModalFooter,
  ModalHeader,
} from "../../../design-system/components/composite/Modal/Modal";
import { Button } from "../../../design-system/components/primitives/Button/Button";
import {
  borderRadius,
  borderStyle,
  borderWidth,
} from "../../../design-system/tokens/borders";
import { rawColors } from "../../../design-system/tokens/colors";
import { shadows } from "../../../design-system/tokens/shadows";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";

/**
 * Design F04: 760 wide, radius 20, padding 28, gap 20, no header divider or shadow.
 *
 * `$allowOverflow` lifts `Modal`'s own `overflow: hidden` (this className lands
 * on that element). A step body's popover has to clear *two* clipping
 * ancestors, not one: the body region and the dialog itself. Without this the
 * sources list is cut off at the shell edge no matter which direction it opens.
 * Off by default so the recipe step, whose card grid can outgrow the shell, keeps clipping.
 */
export const Shell = styled(Modal)<{ $allowOverflow?: boolean }>`
  /* GAP: design 760px; no width token. */
  width: min(47.5rem, 100%);
  margin-inline: auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${spacing[5]};
  padding: ${spacing[7]};
  border-radius: ${borderRadius[20]};
  border: none;
  box-shadow: ${shadows.component.createStoryModal};
  ${({ $allowOverflow }) => $allowOverflow && "overflow: visible;"}
`;

export const Header = styled(ModalHeader)`
  padding: 0;
  border: none;
  box-shadow: none;
`;

export const HeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
`;

/* GAP: design 20/28 weight 600. */
export const Title = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 1.25rem;
  line-height: 1.75rem;
  font-weight: 600;
`;

/* GAP: design 13px. */
export const Subtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.8125rem;
`;

/* Hairline above the footer, 16px of air below it (the buttons carry the margin in the design). */
export const Footer = styled(ModalFooter)`
  align-items: center;
  gap: ${spacing[2.5]};
  padding: ${spacing[4]} 0 0;
  border-top: ${borderWidth[1]} ${borderStyle.solid} ${rawColors.stroke[200]};
`;

export const FooterNote = styled.span`
  flex: 1;
  color: ${rawColors.text.lowEmphasis};
  ${typographyMixin("caption", 4)}
`;

/* GAP: design 38px tall, 13/600. */
export const SecondaryAction = styled.button`
  height: 2.375rem;
  padding: 0 1.125rem;
  border: ${borderWidth[1]} ${borderStyle.solid} ${rawColors.stroke[200]};
  border-radius: ${borderRadius.full};
  background: ${rawColors.structural.white};
  color: ${rawColors.text.highEmphasis};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
`;

export const PrimaryAction = styled(Button)`
  height: 2.375rem;
  padding: 0 ${spacing[5]};
  border-radius: ${borderRadius.full};
  font-size: 0.8125rem;
  font-weight: 600;
  flex-shrink: 0;
`;
